import React, { useEffect, useMemo, useState } from "react";
import { PROD_TABS, PARALLEL_STAGE_TRACKS } from "./production_config";
import { Package, Cog, Clock, AlertTriangle, Eye, Lock, Search, Store, Warehouse, PencilRuler, Scissors, Printer, Layers, CheckCircle2, Flame, Sparkle, HelpCircle, Image as ImageIcon } from "lucide-react";

// Iter 462 — API base untuk fetch thumbnail mockup (reuse endpoint existing
//   `/orders/{number}/mockup.preview` yang sudah dipakai `QueueMockupThumb` di
//   production_queue.js). Tidak membuat endpoint baru.
const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Format tanggal ID (dd MMM yyyy). Local helper agar file self-contained.
const MONTHS_ID = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agu","Sep","Okt","Nov","Des"];
const fmtDateID = (iso) => {
  if (!iso) return "—";
  try {
    const [y,m,d] = String(iso).split("-").map(Number);
    if (!y||!m||!d) return iso;
    return `${d} ${MONTHS_ID[m-1]||""} ${y}`;
  } catch { return iso; }
};
// Iter 460 — Halaman Produksi fokus progres pengerjaan: helper rupiah & WA
//   tidak lagi digunakan di sini (dipindah ke menu Pesanan). Dihapus agar tidak
//   menyisakan dead code.

const STAGE_ICON = {
  "Toko": Store,
  "Gudang": Warehouse,
  "Mockup": PencilRuler,
  "Bordir": Scissors,
  "Sablon/DTF": Printer,
  "Mesin Press": Flame,
  "Jersey/Sublim": Layers,
  "Finishing": Sparkle,
  "Selesai": CheckCircle2,
  "Perlu Diperiksa": Layers,
};

// Iter 244 — Mapping stage paralel → divisi/tim yang benar-benar mengerjakan.
//   Sumber tunggal: `PARALLEL_STAGE_TRACKS` dari production_config (agar konsisten
//   dgn workflow nyata, mis. Sablon Paralel & Persiapan Paralel). "Sablon Plastisol"
//   bukan paralel — tapi stage sekuensial di route "Bordir + Sablon (Plastisol)"
//   yang dikerjakan tim Sablon/DTF → di-map manual.
//   Dipakai HANYA di sisi counter/filter antrean per divisi. `allCards` (source of
//   truth untuk KPI unique Produk Aktif/Sedang Diproses) TIDAK diduplikasi.
const PARALLEL_STAGE_TO_TABS = {
  ...Object.fromEntries(
    Object.entries(PARALLEL_STAGE_TRACKS).map(([stage, tracks]) =>
      [stage, Array.from(new Set(tracks.map(t => t.team)))]
    )
  ),
  "Sablon Plastisol": ["Sablon/DTF"],
};
const _NON_TAB_STAGES = new Set(["Perlu Diperiksa", "Selesai"]);
const stageToTabs = (stage) => {
  const s = String(stage || "").trim();
  if (!s || _NON_TAB_STAGES.has(s)) return [];
  if (PARALLEL_STAGE_TO_TABS[s]) return PARALLEL_STAGE_TO_TABS[s];
  return [s]; // stage biasa = tab dgn nama sama
};

// Priority pill color mapping (deadline_status dari backend: Aman/Hampir Deadline/Terlambat/Selesai)
const PRIORITY_ORDER = { "Terlambat":0, "Hampir Deadline":1, "Aman":2, "Selesai":3 };

// Admin Toko: read-only oversight view untuk seluruh tahap produksi.
// Card berisi Nomor Nota · Customer · Produk+Qty · Deadline · Tahap · Prioritas · Lihat Detail.
export function ProductionQueueAdminToko({ orders, operator, onOpen, onOperatorLogout, onOpenMaster }) {
  // Iter 256 — Single active filter untuk tile + tab. Value:
  //   "all" → tampilkan semua produk aktif
  //   "sedang" | "hampir" | "terlambat" → filter dari tile atas
  //   "<stage-key>" (Mockup/Toko/Gudang/Bordir/…) → filter dari kartu Antrean per Proses
  //   NB: key "hampir" dipertahankan untuk kompatibilitas internal, tetapi
  //   label + logic di Iter 500 beralih ke "Deadline Hari Ini".
  const [activeFilter, setActiveFilter] = useState("all");
  const [q, setQ] = useState("");
  // Iter 500 — Tanggal hari ini (WIB) sebagai single source-of-truth untuk
  //   KPI "Deadline Hari Ini". Format YYYY-MM-DD, sama dengan `order.deadline`.
  const todayISO = useMemo(() =>
    new Intl.DateTimeFormat("en-CA", { timeZone:"Asia/Jakarta" }).format(new Date()),
  []);
  // Bangun list card produk aktif (kecuali Draft/Dibatalkan) — sekali di-memo.
  // Iter 233 — Antrian Toko HANYA menampilkan produk yang masih benar-benar aktif.
  //   EXCLUDE:
  //     - pickup_status === "Diambil" (sudah diambil pelanggan)
  //     - order_status === "Selesai"  (nota selesai; dipantau di Pesanan → Selesai—Belum Diambil)
  //     - order_status === "Dibatalkan" | "Draft"
  //   Semua counter Toko (Produk Aktif, Sedang Diproses, tab-per-tahap) otomatis konsisten
  //   dgn daftar yang tampil karena semua derive dari `allCards`.
  const allCards = useMemo(() => {
    const out = [];
    for (const o of (orders||[])) {
      if (!o) continue;
      const os = String(o.order_status||"");
      if (os === "Draft" || os === "Dibatalkan" || os === "Selesai") continue;
      if (o.pickup_status === "Diambil") continue;
      if (o.is_test === true) continue;
      for (const p of (o.products||[])) {
        out.push({
          key: `${o.order_number}::${p.id||p.name}`,
          order: o,
          product: p,
          stage: p.production_stage || "Perlu Diperiksa",
          priority: p.deadline_status || "Aman",
          orderStatus: os, // Iter 256 — dipakai exclude Menunggu Finalisasi dari Sedang Diproses
        });
      }
    }
    return out;
  }, [orders]);

  // Ringkasan angka atas (Admin Toko oversight)
  const summary = useMemo(() => {
    let prodAktif = 0, sedangDiproses = 0, deadlineHariIni = 0, terlambat = 0;
    for (const c of allCards) {
      if (c.stage !== "Selesai") prodAktif += 1;
      // Iter 244 (final check) — "Sedang Diproses" hanya hitung produk yang benar-benar
      //   punya proses aktif: stage terisi (non-empty) & bukan Selesai/Perlu Diperiksa.
      // Iter 256 — Tambahan exclude: nota `Menunggu Finalisasi` (semua produk sudah Selesai,
      //   tinggal QC final) tidak dihitung sebagai proses aktif.
      const _st = String(c.stage || "").trim();
      if (_st && _st !== "Selesai" && _st !== "Perlu Diperiksa" && c.orderStatus !== "Menunggu Finalisasi") {
        sedangDiproses += 1;
      }
      // Iter 500 — KPI "Deadline Hari Ini" (sebelumnya "Hampir Deadline").
      //   Hitung HANYA produk aktif yang `order.deadline === hari ini (WIB)`
      //   dan belum Terlambat (karena `allCards` sudah exclude Selesai/Diambil/
      //   Dibatalkan/Draft, cukup cek deadline + priority != Terlambat).
      //   Source-of-truth = field `order.deadline` + `todayISO`, konsisten dengan
      //   `isOverdue` pada kartu (lihat `derivePriorityLabel` H-ago).
      if (c.priority !== "Terlambat"
          && c.order.deadline
          && c.order.deadline === todayISO) {
        deadlineHariIni += 1;
      }
      if (c.priority === "Terlambat") terlambat += 1;
    }
    return { prodAktif, sedangDiproses, deadlineHariIni, terlambat };
  }, [allCards, todayISO]);

  // Tabs tahap: dari PROD_TABS. Perlu Diperiksa disembunyikan (tugas Owner/Admin).
  //   Iter 244 — Counter tab hitung produk yang stage-nya mapped ke tab tsb via
  //   `stageToTabs()`. Produk paralel bisa muncul di beberapa tab (sesuai realitas
  //   antrean per divisi). KPI Produk Aktif/Sedang Diproses TETAP unique product.
  //   Iter 379 — Dedup by `t.key` karena PROD_TABS punya dua entry "Bordir"
  //   (team desainer vs Operator Bordir fisik). Admin cukup satu tab per stage
  //   produksi; label mengikuti entry pertama (team desainer → "Desain Bordir").
  const stageTabs = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const t of PROD_TABS) {
      if (t.key === "Perlu Diperiksa") continue;
      if (seen.has(t.key)) continue;
      seen.add(t.key);
      const count = allCards.filter(c => stageToTabs(c.stage).includes(t.key)).length;
      out.push({ key: t.key, label: t.label, count });
    }
    return out;
  }, [allCards]);

  // Iter 244 — Audit: daftar CARD produk aktif ber-stage yang TIDAK terpetakan ke tab
  //   manapun. Deteksi ini mencegah produk "menghilang" dari dashboard karena stage baru.
  //   Banner clickable → daftar produk unmapped ikut ditampilkan (bukan hanya warning).
  const unmappedCards = useMemo(() => {
    const tabKeys = new Set(PROD_TABS.filter(t => t.key !== "Perlu Diperiksa").map(t => t.key));
    return allCards.filter(c => {
      const s = String(c.stage || "").trim();
      if (!s || s === "Selesai" || s === "Perlu Diperiksa") return false;
      const mapped = stageToTabs(s);
      return !mapped.some(k => tabKeys.has(k));
    });
  }, [allCards]);
  const unmappedActiveStages = useMemo(() => {
    const map = new Map();
    for (const c of unmappedCards) {
      const s = String(c.stage || "").trim();
      map.set(s, (map.get(s) || 0) + 1);
    }
    return Array.from(map.entries()).map(([stage, count]) => ({ stage, count }));
  }, [unmappedCards]);
  const [showUnmapped, setShowUnmapped] = useState(false);

  const query = q.trim().toLowerCase();
  // Iter 256 — Filter kartu berdasarkan `activeFilter` (single state, tile + tab):
  //   "all" → semua produk aktif
  //   "sedang" → hanya produk yang benar-benar diproses (stage non-empty, bukan Selesai/Perlu Diperiksa,
  //              order bukan Menunggu Finalisasi) — konsisten dgn KPI Sedang Diproses
  //   "hampir" / "terlambat" → filter berdasarkan priority (deadline_status backend)
  //   "<stage-key>" → produk yg stage-nya termapping ke tab tsb via stageToTabs()
  const cardsFiltered = useMemo(() => {
    let rows;
    if (activeFilter === "all") rows = allCards;
    else if (activeFilter === "sedang") rows = allCards.filter(c => {
      const _st = String(c.stage || "").trim();
      return _st && _st !== "Selesai" && _st !== "Perlu Diperiksa" && c.orderStatus !== "Menunggu Finalisasi";
    });
    else if (activeFilter === "hampir") rows = allCards.filter(c =>
      c.priority !== "Terlambat"
      && c.order.deadline
      && c.order.deadline === todayISO
    );
    else if (activeFilter === "terlambat") rows = allCards.filter(c => c.priority === "Terlambat");
    else rows = allCards.filter(c => stageToTabs(c.stage).includes(activeFilter));
    const filtered = query
      ? rows.filter(c => {
          const on = String(c.order.order_number||"").toLowerCase();
          const cn = String(c.order.customer_name||"").toLowerCase();
          const pn = String(c.product.custom_name || c.product.name || "").toLowerCase();
          return on.includes(query) || cn.includes(query) || pn.includes(query);
        })
      : rows;
    // Urut: prioritas terlambat > hampir > aman > selesai, lalu tanggal deadline.
    return [...filtered].sort((a,b) => {
      const pa = PRIORITY_ORDER[a.priority] ?? 2;
      const pb = PRIORITY_ORDER[b.priority] ?? 2;
      if (pa !== pb) return pa - pb;
      return String(a.order.deadline||"").localeCompare(String(b.order.deadline||""));
    });
  }, [allCards, activeFilter, query, todayISO]);
  // Iter 256 — Toggle handler: klik ulang filter yang sedang aktif → reset ke "all".
  const toggleFilter = (key) => setActiveFilter(prev => prev === key ? "all" : key);

  return <div className="at-prod-wrap" data-testid="at-prod-wrap">
    {operator && <div className="at-prod-toolbar">
      {onOpenMaster && <button
        type="button"
        className="at-prod-master text-btn small-btn"
        data-testid="at-prod-master-btn"
        onClick={onOpenMaster}
        aria-label="Kelola Master Produk"
      ><Package size={13}/>Kelola Produk</button>}
      <button
        type="button"
        className="at-prod-lock text-btn small-btn"
        data-testid="at-prod-lock-btn"
        onClick={onOperatorLogout}
        aria-label="Ganti operator atau kunci layar"
      ><Lock size={13}/>Ganti Operator</button>
    </div>}

    <div className="at-prod-summary" data-testid="at-prod-summary">
      <SummaryCard testid="at-prod-sum-aktif" icon={<Package size={18}/>} label="Produk Aktif" value={summary.prodAktif} tone="ink"
        active={activeFilter === "all"} onClick={() => setActiveFilter("all")}/>
      <SummaryCard testid="at-prod-sum-proses" icon={<Cog size={18}/>} label="Sedang Diproses" value={summary.sedangDiproses} tone="blue"
        active={activeFilter === "sedang"} onClick={summary.sedangDiproses > 0 ? () => toggleFilter("sedang") : null}/>
      <SummaryCard testid="at-prod-sum-hampir" icon={<Clock size={18}/>} label="Deadline Hari Ini" value={summary.deadlineHariIni} tone="amber"
        active={activeFilter === "hampir"} onClick={summary.deadlineHariIni > 0 ? () => toggleFilter("hampir") : null}/>
      <SummaryCard testid="at-prod-sum-terlambat" icon={<AlertTriangle size={18}/>} label="Terlambat" value={summary.terlambat} tone="red"
        active={activeFilter === "terlambat"} onClick={summary.terlambat > 0 ? () => toggleFilter("terlambat") : null}/>
    </div>

    <div className="at-prod-tabs" role="tablist" data-testid="at-prod-tabs" aria-labelledby="at-prod-tabs-heading">
      <div id="at-prod-tabs-heading" className="at-prod-tabs-heading" data-testid="at-prod-tabs-heading"
        style={{gridColumn:"1 / -1", display:"flex", flexDirection:"column", gap:"2px", marginBottom:"6px"}}>
        <span style={{fontWeight:700, fontSize:"14px", display:"inline-flex", alignItems:"center", gap:"6px"}}>
          Antrean per Proses
          {/* Iter 403 — Penjelasan teknis dipindah ke tooltip. Teks utama ringkas. */}
          <span className="at-prod-info" tabIndex={0}
            title="Produk paralel bisa muncul di beberapa divisi, sehingga total antrean dapat lebih besar dari Produk Aktif. Klik kartu untuk filter, klik lagi untuk reset."
            aria-label="Info antrean per proses"
            data-testid="at-prod-tabs-info"
            style={{display:"inline-flex", alignItems:"center", color:"var(--muted,#64748b)", cursor:"help"}}>
            <HelpCircle size={13}/>
          </span>
        </span>
        <span className="muted small">Jumlah pekerjaan aktif di setiap bagian.</span>
      </div>
      {stageTabs.map(t => {
        const Icon = STAGE_ICON[t.key] || Layers;
        const isActive = activeFilter === t.key;
        return <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={isActive}
          data-testid={`at-prod-tab-${t.key.replaceAll(" ","-").replaceAll("/","-")}`}
          className={`at-prod-tab ${isActive?"active":""}`}
          onClick={()=>toggleFilter(t.key)}
        >
          <span className="at-prod-tab-icon"><Icon size={16}/></span>
          <span className="at-prod-tab-body">
            <span className="at-prod-tab-name">{t.label}</span>
            <span className="at-prod-tab-count" data-testid={`at-prod-tab-count-${t.key.replaceAll(" ","-").replaceAll("/","-")}`}>{t.count}</span>
          </span>
        </button>;
      })}
    </div>

    {/* Iter 244 — Audit banner: stage aktif yang belum terpetakan ke tab manapun.
        Ditampilkan hanya kalau ada (aman dari hilangnya produk secara diam-diam). */}
    {unmappedActiveStages.length > 0 && <div className="at-prod-unmapped" data-testid="at-prod-unmapped"
      style={{display:"flex", flexDirection:"column", gap:"8px", padding:"10px 12px", margin:"8px 0",
              background:"#fff7ed", border:"1px solid #fdba74", borderRadius:"10px", color:"#7c2d12", fontSize:"13px"}}>
      <div style={{display:"flex", alignItems:"flex-start", gap:"8px"}}>
        <AlertTriangle size={14} style={{marginTop:"2px", flex:"none"}}/>
        <span style={{flex:1}}>
          <b>Perhatian:</b> {unmappedCards.length} produk aktif ber-stage yang belum terpetakan ke tab divisi:{" "}
          {unmappedActiveStages.map(x => <code key={x.stage} style={{background:"#fed7aa", padding:"1px 6px", borderRadius:"6px", marginRight:"4px"}}>{x.stage} ({x.count})</code>)}
          . Produk ini tetap dihitung di KPI <b>Produk Aktif</b>.
        </span>
        <button type="button"
          data-testid="at-prod-unmapped-toggle"
          onClick={()=>setShowUnmapped(v => !v)}
          style={{background:"#7c2d12", color:"#fff", border:"none", borderRadius:"8px", padding:"6px 10px", cursor:"pointer", fontSize:"12px", fontWeight:600, whiteSpace:"nowrap"}}
        >{showUnmapped ? "Sembunyikan" : "Lihat produk"}</button>
      </div>
      {showUnmapped && <div data-testid="at-prod-unmapped-list" style={{display:"flex", flexDirection:"column", gap:"4px", marginLeft:"22px", background:"#fffaf5", padding:"8px 10px", borderRadius:"8px", border:"1px dashed #fdba74"}}>
        {unmappedCards.map(c => <div key={c.key} style={{display:"flex", gap:"10px", alignItems:"center", fontSize:"12px"}}>
          <span style={{fontWeight:700}}>{c.order.order_number}</span>
          <span style={{color:"#7c2d12"}}>· {c.product.custom_name || c.product.name || "—"}</span>
          <span style={{marginLeft:"auto", background:"#fed7aa", padding:"1px 6px", borderRadius:"6px", fontWeight:600}}>{c.stage}</span>
          <button type="button" onClick={()=>onOpen(c.order)}
            style={{background:"transparent", border:"1px solid #fdba74", color:"#7c2d12", borderRadius:"6px", padding:"2px 8px", cursor:"pointer", fontSize:"11px"}}
          >Detail</button>
        </div>)}
      </div>}
    </div>}

    <div className="at-prod-search" data-testid="at-prod-search">
      <Search size={15}/>
      <input
        type="text"
        placeholder="Cari nota, pelanggan, atau produk"
        value={q}
        onChange={e=>setQ(e.target.value)}
        data-testid="at-prod-search-input"
        aria-label="Cari antrean produksi"
      />
    </div>

    <div className="at-prod-list" data-testid="at-prod-list">
      {cardsFiltered.length === 0
        ? <div className="at-prod-empty" data-testid="at-prod-empty">
            <div className="at-prod-empty-icon"><Package size={22}/></div>
            <p className="at-prod-empty-title">Tidak ada produk pada filter ini.</p>
            <p className="muted small">Klik ulang filter yang aktif atau tekan <b>Produk Aktif</b> untuk menampilkan semua.</p>
          </div>
        : cardsFiltered.map(c => <ProdCard key={c.key} card={c} onOpen={()=>onOpen(c.order)}/>)}
    </div>
  </div>;
}

function SummaryCard({ icon, label, value, tone, testid, active, onClick }) {
  const clickable = typeof onClick === "function";
  const cls = `at-prod-sum at-prod-sum-${tone||"ink"}${active ? " active" : ""}${clickable ? " clickable" : ""}`;
  if (!clickable) return <article className={cls} data-testid={testid}>
    <span className="at-prod-sum-icon">{icon}</span>
    <span className="at-prod-sum-value">{value}</span>
    <span className="at-prod-sum-label">{label}</span>
  </article>;
  return <button type="button" className={cls} data-testid={testid} onClick={onClick} aria-pressed={!!active}>
    <span className="at-prod-sum-icon">{icon}</span>
    <span className="at-prod-sum-value">{value}</span>
    <span className="at-prod-sum-label">{label}</span>
  </button>;
}

// Iter 462 — Thumbnail mockup compact (reuse endpoint `/orders/{num}/mockup.preview`
//   dari production_queue.js). Fallback placeholder "Belum ada mockup" bila
//   gambar gagal dimuat (nota belum punya file mockup).
function ProdCardThumb({ orderNumber, onOpen, testId }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [orderNumber]);
  const url = `${API_BASE}/orders/${encodeURIComponent(orderNumber || "")}/mockup.preview?_=t`;
  return (
    <button
      type="button"
      className={`at-prod-thumb${failed ? " at-prod-thumb-empty" : ""}`}
      onClick={(e)=>{ e.stopPropagation(); if (onOpen) onOpen(); }}
      data-testid={testId || undefined}
      aria-label={`Pratinjau mockup pesanan ${orderNumber}`}>
      {!failed ? (
        <img src={url} alt="" draggable="false" loading="lazy" decoding="async"
          onError={() => setFailed(true)}/>
      ) : (
        <span className="at-prod-thumb-ph">
          <ImageIcon size={18} aria-hidden="true"/>
          <span>Belum ada mockup</span>
        </span>
      )}
    </button>
  );
}

function ProdCard({ card, onOpen }) {
  const { order, product, stage, priority } = card;
  const productLabel = product.custom_name || product.name || "Produk (tanpa nama)";
  const qty = Number(product.quantity||0);
  // Iter 97 — Kelas prioritas + label untuk chip deadline.
  const prioClass = priority === "Terlambat" ? "prio-late"
    : priority === "Hampir Deadline" ? "prio-warn"
    : priority === "Selesai" ? "prio-done"
    : "prio-safe";
  // Iter 461 — Produksi = fokus monitoring. Pengambilan sepenuhnya pindah ke
  //   menu Pesanan/Finalisasi. Status picked masih dipakai untuk logic badge
  //   Terlambat / Deadline Hari Ini (jangan tampil kalau sudah diambil).
  const isPicked = (order.pickup_status || "Belum Diambil") === "Diambil";
  // Iter 97 — PIC tahap aktif. Iter 403 — Jangan tampilkan "—" ambigu;
  //   fallback eksplisit "Belum ada operator" agar user langsung paham status.
  const picRaw = String(product.production_pic || "").trim();
  const pic = picRaw
    || (stage === "Finishing" ? "Ega"
      : stage === "Selesai" ? "Selesai"
      : "");
  const picEmpty = pic === "";
  // Iter 99 — Pin merah Deadline Hari Ini (skip kalau sudah selesai / picked up).
  const todayISO = new Intl.DateTimeFormat("en-CA", { timeZone:"Asia/Jakarta" }).format(new Date());
  const isDeadlineToday = !!(order.deadline && order.deadline === todayISO && !isPicked && priority !== "Selesai");
  // Iter 102 — Chip oranye Terlambat (deadline lampau + belum diambil).
  const isOverdue = !!(order.deadline && order.deadline < todayISO && !isPicked);
  // Iter 104 — Hitung hari telat.
  const daysLate = isOverdue ? Math.max(1, Math.round((new Date(todayISO) - new Date(order.deadline)) / 86400000)) : 0;
  // Iter 462 — Hilangkan chip prioritas redundan di baris deadline jika pin
  //   "Terlambat · N hari" / "Deadline Hari Ini" sudah tampil di kanan atas.
  const hasHeaderBadge = isOverdue || isDeadlineToday;
  const showPrioChip = !hasHeaderBadge && priority && priority !== "Normal";

  return <article className="at-prod-card at-prod-card-slim at-prod-card-flat" data-testid={`at-prod-card-${order.order_number}-${product.id||"x"}`}>
    {/* Iter 462 — Grid 2 kolom: thumbnail (kiri) + konten (kanan). */}
    <div className="at-prod-flat-grid">
      <ProdCardThumb
        orderNumber={order.order_number}
        onOpen={onOpen}
        testId={`at-prod-card-thumb-${order.order_number}-${product.id||"x"}`}
      />
      <div className="at-prod-flat-main">
        {/* Baris 1 — Nomor Nota + Pin keterlambatan/hari ini */}
        <header className="at-prod-card-head">
          <span className="at-prod-nota" data-testid={`at-prod-card-nota-${order.order_number}`}>{order.order_number}</span>
          <div className="at-prod-card-head-right">
            {isOverdue && <span className="order-card-overdue-pin" data-testid={`at-prod-card-overdue-${order.order_number}`} title={`Deadline ${daysLate} hari lalu`}>
              <span className="order-card-overdue-dot"/>Terlambat · {daysLate} hari
            </span>}
            {isDeadlineToday && <span className="order-card-today-pin" data-testid={`at-prod-card-today-${order.order_number}`} title="Deadline hari ini">
              <span className="order-card-today-dot"/>Deadline Hari Ini
            </span>}
          </div>
        </header>

        {/* BODY — flat hierarchy: customer → produk → tahap+PIC → deadline */}
        <div className="at-prod-card-body">
          <div className="at-prod-flat-cust" data-testid={`at-prod-card-cust-${order.order_number}`}>
            {order.customer_name || "—"}
          </div>
          <div className="at-prod-flat-prod">
            <span data-testid={`at-prod-card-product-${order.order_number}-${product.id||"x"}`}>{productLabel}</span>
            {qty>0 && <span className="at-prod-flat-qty"> · {qty} pcs</span>}
          </div>
          <div className="at-prod-flat-stage">
            <span className="at-prod-stage-chip" data-testid={`at-prod-card-stage-${order.order_number}-${product.id||"x"}`}>{stage}</span>
            <span className={`at-prod-flat-pic${picEmpty ? " at-prod-pic-empty" : ""}`}
              data-testid={`at-prod-card-pic-${order.order_number}-${product.id||"x"}`}>
              {picEmpty ? "Belum ada operator" : `Oleh: ${pic}`}
            </span>
          </div>
          <div className="at-prod-flat-deadline">
            <Clock size={13}/>
            <span className="at-prod-flat-date" data-testid={`at-prod-card-deadline-${order.order_number}-${product.id||"x"}`}>{fmtDateID(order.deadline)}</span>
            {showPrioChip && <span className={`at-prod-prio ${prioClass}`} data-testid={`at-prod-card-prio-${order.order_number}-${product.id||"x"}`}>{priority}</span>}
          </div>
        </div>
      </div>
    </div>

    {/* FOOTER — tombol Detail full-width */}
    <footer className="at-prod-card-foot">
      <button type="button" className="soft-btn small-btn at-prod-detail-full" data-testid={`at-prod-card-detail-${order.order_number}-${product.id||"x"}`} onClick={onOpen}>
        <Eye size={14}/>Detail
      </button>
    </footer>
  </article>;
}
